import type { Conversation } from "@dtelecom/secure-chat-client"
import { createSelector } from "@reduxjs/toolkit"

import { classifyConversation } from "../contacts/classifyConversation"
import type { ChatState } from "./chatStore"

interface PartitionedConversations {
  contacts: Conversation[]
  requests: Conversation[]
  contactsUnreadCount: number
  requestsUnreadCount: number
}

export const selectConnectionState = (s: ChatState) => s.connectionState

export const selectTabRole = (s: ChatState) => s.tabRole

export const selectChatPhase = (s: ChatState) => s.phase

export const selectSearchQuery = (s: ChatState) => s.searchQuery

export const selectPartitionedConversations = createSelector(
  [
    (s: ChatState) => s.conversations,
    (s: ChatState) => s.blockedIds,
    (s: ChatState) => s.requestIds,
    (s: ChatState) => s.notFoundIds,
  ],
  (
    conversations,
    blockedIds,
    requestIds,
    notFoundIds,
  ): PartitionedConversations => {
    const contacts: Conversation[] = []
    const requests: Conversation[] = []
    let contactsUnreadCount = 0
    let requestsUnreadCount = 0
    const ctx = { blockedIds, requestIds }

    for (const conversation of conversations) {
      if (notFoundIds.has(conversation.peerUserId)) continue

      const bucket = classifyConversation(conversation, ctx)
      if (bucket === "contact") {
        contacts.push(conversation)
        contactsUnreadCount += conversation.unreadCount > 0 ? 1 : 0
      } else if (bucket === "request") {
        requests.push(conversation)
        requestsUnreadCount += conversation.unreadCount > 0 ? 1 : 0
      }
    }

    return {
      contacts,
      contactsUnreadCount,
      requests,
      requestsUnreadCount,
    }
  },
)

export const selectThread = (peerId: string | null) => (s: ChatState) =>
  peerId ? (s.threadsByPeer.get(peerId) ?? null) : null
